import { ClinicianDashboard, CliniciansGetClinicianDashboardData } from '@bioliberty/lifemind';
import { getQueryCacheKey } from '../util';
import { formatUtcDatesAsLocal } from '../util/date';
import { HttpMethod } from '../util/http';
import { tms } from './tms';

export type GetClinicianDashboardParams = Omit<
  NonNullable<CliniciansGetClinicianDashboardData['query']>,
  'tenantIds' | 'sessionsPage' | 'sessionsPageSize'
> & {
  tenantIds?: string[];
  sessionsPage?: number;
  sessionsPageSize?: number;
};

export const cliniciansApi = tms.injectEndpoints({
  endpoints: (builder) => ({
    getClinicianDashboard: builder.query<ClinicianDashboard, GetClinicianDashboardParams>({
      query: (params) => ({
        url: `clinicians/me/dashboard`,
        method: HttpMethod.Get,
        params: {
          ...params,
          tenantIds: params.tenantIds?.join(',')
        }
      }),
      transformResponse: (response: ClinicianDashboard) => ({
        ...response,
        sessions: response.sessions.map((session) => ({
          ...session,
          note: session.note ? formatUtcDatesAsLocal(session.note) : session.note
        }))
      }),
      providesTags: () => [{ type: 'Clinicians', id: 'me-dashboard' }],
      // Keeps the cache key consistent for different pages of sessions
      serializeQueryArgs: ({ queryArgs }) => {
        const { sessionsPage: _sessionsPage, ...params } = queryArgs;
        return getQueryCacheKey(params, 'me-dashboard');
      },
      // Merges paginated session and patient data
      merge: ({ sessions, patients }, newValue) => {
        const mergeSessions = () => {
          const sessionMap = new Map(sessions.map((session) => [session.sessionId, session]));
          newValue.sessions.forEach((session) => {
            sessionMap.set(session.sessionId, session);
          });

          return Array.from(sessionMap.values());
        };

        const mergePatients = () => {
          const existingPatientIds = new Set(patients.map((p) => p.id));
          const newPatients = newValue.patients.filter((p) => !existingPatientIds.has(p.id));
          return [...patients, ...newPatients];
        };

        return {
          ...newValue,
          sessions: mergeSessions(),
          patients: mergePatients()
        };
      },
      // Refetches when the sessions page changes
      forceRefetch: ({ currentArg, previousArg }) => currentArg?.sessionsPage !== previousArg?.sessionsPage
    })
  }),
  overrideExisting: false
});

export const { useGetClinicianDashboardQuery } = cliniciansApi;
